import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle, Clock, RefreshCw, XCircle } from "lucide-react";
import { ensureReplica } from "@/api/endpoints/ha";
import type { ReplicaStatus, VM, VMReplica } from "@/types";

interface Props {
  vm: VM;
}

const STATUS_META: Record<
  ReplicaStatus,
  { label: string; cls: string; icon: typeof CheckCircle }
> = {
  up_to_date: { label: "Up to date", cls: "bg-emerald-600/20 text-emerald-300", icon: CheckCircle },
  syncing:    { label: "Syncing",    cls: "bg-sky-600/20 text-sky-300",         icon: RefreshCw },
  pending:    { label: "Pending",    cls: "bg-amber-600/20 text-amber-300",     icon: Clock },
  stale:      { label: "Stale",      cls: "bg-amber-600/20 text-amber-300",     icon: AlertTriangle },
  unsatisfied:{ label: "Unsatisfied",cls: "bg-orange-600/20 text-orange-300",   icon: AlertTriangle },
  failed:     { label: "Failed",     cls: "bg-red-600/20 text-red-300",         icon: XCircle },
};

export function ReplicaPanel({ vm }: Props) {
  if (vm.service_tier === "A") return null;

  const qc = useQueryClient();
  const [lastResult, setLastResult] = useState<VMReplica | null>(null);

  const ensureMut = useMutation({
    mutationFn: () => ensureReplica(vm.id),
    onSuccess: (data) => {
      setLastResult(data);
      qc.invalidateQueries({ queryKey: ["vm", vm.id] });
      qc.invalidateQueries({ queryKey: ["vms"] });
    },
  });

  const replica = vm.replica;
  const status = replica?.status;
  const meta = status ? STATUS_META[status] : null;
  const Icon = meta?.icon ?? AlertTriangle;

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 font-semibold text-gray-100">
            <Icon className="h-4 w-4" />
            High availability replica
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Tier {vm.service_tier} · pvesr job on another Proxmox node. Data
            protection only — IP failover requires the LB layer.
          </p>
        </div>
        <button
          onClick={() => ensureMut.mutate()}
          disabled={ensureMut.isPending}
          className="flex items-center gap-1 rounded border border-gray-700 bg-gray-800 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={"h-3.5 w-3.5 " + (ensureMut.isPending ? "animate-spin" : "")} />
          {ensureMut.isPending ? "Running…" : replica ? "Re-sync" : "Set up"}
        </button>
      </div>

      {!replica && (
        <div className="rounded border border-gray-800 bg-gray-950 p-3 text-sm text-gray-400">
          No replica configured yet. Click <b>Set up</b> to schedule one.
        </div>
      )}

      {replica && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Item label="Status">
            <span className={"rounded px-1.5 py-0.5 text-xs " + (meta?.cls ?? "")}>
              {meta?.label ?? status}
            </span>
          </Item>
          <Item label="Replica node">
            <code className="text-gray-200">
              {replica.replica_node || <span className="text-gray-500">—</span>}
            </code>
          </Item>
          <Item label="Mode">
            <span className="text-gray-300">{replica.replication_mode}</span>
          </Item>
          <Item label="Schedule">
            <code className="text-gray-300">{replica.replication_schedule}</code>
          </Item>
          <Item label="Last sync">
            <span className="text-gray-300">
              {replica.last_sync_at
                ? new Date(replica.last_sync_at).toLocaleString()
                : <span className="text-gray-500">never</span>}
            </span>
          </Item>
          <Item label="Proxmox job">
            <code className="text-gray-300">
              {replica.proxmox_job_id || <span className="text-gray-500">—</span>}
            </code>
          </Item>
          {replica.last_error && (
            <div className="col-span-2 rounded border border-red-800/40 bg-red-900/20 px-2 py-1 text-xs text-red-300">
              {replica.last_error}
            </div>
          )}
        </dl>
      )}

      {ensureMut.isError && (
        <div className="mt-3 rounded border border-red-800/40 bg-red-900/20 p-2 text-xs text-red-300">
          {ensureMut.error instanceof Error
            ? ensureMut.error.message
            : "Replica setup failed"}
        </div>
      )}

      {lastResult && ensureMut.isSuccess && (
        <div className="mt-3 text-xs text-gray-500">
          Replica updated · status now <b>{lastResult.status}</b>
          {lastResult.replica_node ? ` on ${lastResult.replica_node}` : ""}.
        </div>
      )}
    </div>
  );
}

function Item({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
